import { useEffect, useState } from "react";
import { Alert, Form, Modal } from "react-bootstrap";
import { CheckLg, Envelope, XLg } from "react-bootstrap-icons";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";
import { Button as SsButton } from "./Controls/Button";
import { createApiService } from "./shared/apiConfig";
import {
  ServerRequestsAccountEmailFilter,
  SubscriptionStaffApi,
} from "./shared/api/axios-client";

type ActiveParkerEmailFilter = ServerRequestsAccountEmailFilter;

type EmailCopyState = "idle" | "copied" | "error";

const subscriptionStaffApi = createApiService(SubscriptionStaffApi);

const activeParkerEmailFilterOptions: {
  value: ActiveParkerEmailFilter;
  label: string;
  description: string;
}[] = [
  {
    value: ServerRequestsAccountEmailFilter.All,
    label: "All",
    description: "All active-list accounts with email addresses.",
  },
  {
    value: ServerRequestsAccountEmailFilter.AutoPay,
    label: "Auto-pay only",
    description: "Accounts currently billed by auto-pay.",
  },
  {
    value: ServerRequestsAccountEmailFilter.DeferredPayment,
    label: "Deferred Payment only",
    description: "Accounts not currently billed by auto-pay.",
  },
];

type ActiveParkerEmailCopyModalProps = {
  show: boolean;
  onHide: () => void;
  facilityId: number;
};

export function ActiveParkerEmailCopyModal({
  show,
  onHide,
  facilityId,
}: ActiveParkerEmailCopyModalProps) {
  const [activeParkerEmailFilter, setActiveParkerEmailFilter] =
    useState<ActiveParkerEmailFilter>(ServerRequestsAccountEmailFilter.All);
  const [emailCopyState, setEmailCopyState] = useState<EmailCopyState>("idle");
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }

    setEmailCopyState("idle");
    let cancelled = false;

    const loadEmails = async () => {
      setIsLoadingEmails(true);
      try {
        const response = await subscriptionStaffApi.getAccountEmails({
          facilityId,
          filter: activeParkerEmailFilter,
        });
        if (!cancelled) {
          setEmailRecipients(response.data.emails ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setEmailRecipients([]);
          toast.error("Unable to load email list");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEmails(false);
        }
      }
    };

    void loadEmails();
    return () => {
      cancelled = true;
    };
  }, [show, facilityId, activeParkerEmailFilter]);

  const emailRecipientText = emailRecipients.join(", ");
  const activeParkerEmailFilterLabel =
    activeParkerEmailFilterOptions.find(
      (option) => option.value === activeParkerEmailFilter
    )?.label ?? "Selected";
  const activeParkerEmailHeaderLabel =
    activeParkerEmailFilter === ServerRequestsAccountEmailFilter.All
      ? "All accounts"
      : activeParkerEmailFilter === ServerRequestsAccountEmailFilter.AutoPay
        ? "Auto-pay accounts only"
        : "Deferred payment accounts only";

  const handleEmailFilterChange = (filter: ActiveParkerEmailFilter) => {
    setActiveParkerEmailFilter(filter);
    setEmailCopyState("idle");
  };

  const handleGenerateEmailList = async () => {
    if (!emailRecipientText) {
      setEmailCopyState("error");
      return;
    }

    try {
      await navigator.clipboard.writeText(emailRecipientText);
      setEmailCopyState("copied");
    } catch (error) {
      setEmailCopyState("error");
      toast.error("Unable to copy email list");
    }
  };

  const handleResetEmailCopyModal = () => {
    setEmailCopyState("idle");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      contentClassName="rounded-4 overflow-hidden border-0"
    >
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "2.25rem",
              height: "2.25rem",
              backgroundColor: "#fce7f3",
              color: "#ec4899",
            }}
          >
            <Envelope size={18} />
          </div>
          <div>
            <div className="fw-semibold fs-5 lh-sm">Copy account emails</div>
            <div className="text-muted small">
              {activeParkerEmailHeaderLabel}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="btn p-0 text-muted"
          aria-label="Close"
          onClick={onHide}
        >
          <XLg size={18} />
        </button>
      </div>
      <Modal.Body className="p-4">
        <AnimatePresence mode="wait">
          {emailCopyState === "idle" && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <Alert variant="info" className="mb-3">
                Choose which active monthly accounts to include, then click{" "}
                <strong>Generate</strong>. This copies a comma-delimited email
                list to your clipboard.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Email list filter
                </Form.Label>
                <div className="d-grid gap-2">
                  {activeParkerEmailFilterOptions.map((option) => (
                    <Form.Check
                      key={option.value}
                      type="radio"
                      id={`active-parker-email-${option.value}`}
                      name="active-parker-email-filter"
                      checked={activeParkerEmailFilter === option.value}
                      onChange={() => handleEmailFilterChange(option.value)}
                      disabled={isLoadingEmails}
                      label={
                        <span>
                          <span className="fw-semibold">{option.label}</span>
                          <span className="d-block text-muted small">
                            {option.description}
                          </span>
                        </span>
                      }
                    />
                  ))}
                </div>
              </Form.Group>

              <div className="rounded-3 border bg-light p-3 mb-3">
                <div className="fw-semibold">
                  {isLoadingEmails
                    ? "Loading emails..."
                    : `${emailRecipients.length.toLocaleString()} email${
                        emailRecipients.length === 1 ? "" : "s"
                      } ready`}
                </div>
                <div className="text-muted small">
                  Duplicate and blank email addresses are removed before
                  copying.
                </div>
              </div>

              <SsButton
                className="btn-main w-100"
                disabled={isLoadingEmails || emailRecipients.length === 0}
                onClick={handleGenerateEmailList}
              >
                Generate
              </SsButton>
            </motion.div>
          )}

          {emailCopyState === "copied" && (
            <motion.div
              key="copied"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <div
                className="rounded-3 border p-3 mb-4 d-flex align-items-center gap-3"
                style={{
                  backgroundColor: "#ecf7df",
                  borderColor: "#5b8a3c",
                }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "2rem",
                    height: "2rem",
                    backgroundColor: "#ffffff",
                    border: "2px solid #5b8a3c",
                    color: "#5b8a3c",
                  }}
                >
                  <CheckLg size={14} />
                </div>
                <div>
                  <div className="fw-semibold" style={{ color: "#3f6f2a" }}>
                    Ready to paste
                  </div>
                  <div className="small" style={{ color: "#4f7a38" }}>
                    {activeParkerEmailFilterLabel} emails have been generated
                    and copied to your clipboard.
                  </div>
                </div>
              </div>

              <div className="fw-semibold mb-3">How to send</div>
              <div className="d-grid gap-3 mb-4">
                {[
                  "Open your email program and start a new message",
                  "Click the BCC field",
                  "Right-click and paste from your clipboard",
                ].map((step, index) => (
                  <div key={step} className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 small fw-semibold"
                      style={{
                        width: "1.5rem",
                        height: "1.5rem",
                        backgroundColor: "#fce7f3",
                        color: "#ec4899",
                      }}
                    >
                      {index + 1}
                    </div>
                    <div>{step}</div>
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2">
                <SsButton className="flex-fill" onClick={handleResetEmailCopyModal}>
                  Back
                </SsButton>
                <SsButton className="btn-main flex-fill" onClick={onHide}>
                  Done
                </SsButton>
              </div>
            </motion.div>
          )}

          {emailCopyState === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <Alert variant="warning" className="mt-3 mb-0">
                No email list copied. Choose another filter or try Generate
                again.
              </Alert>
              <SsButton
                className="w-100 mt-3"
                onClick={handleResetEmailCopyModal}
              >
                Back
              </SsButton>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal.Body>
    </Modal>
  );
}
